import type { AccountController } from "@hq/client";
import { Button, Input } from "@z/ui";
import { useState } from "react";
import { useAppConfig } from "../AppConfig";
import { useAccountSnapshot } from "./useAccountSnapshot";

export function SignInForm({ account }: { account: AccountController }) {
  const { onReauthenticate } = useAppConfig();
  const state = useAccountSnapshot(account);
  const [busy, setBusy] = useState(false);
  const register = state.setupRequired === true;
  const [error, setError] = useState<string>();
  const pending = busy || state.phase === "checking";
  const message = error ?? state.error;

  // The gateway's sign-in page needs a full-page visit; a form or a retried fetch cannot reach it.
  if (state.accessRequired) {
    return (
      <div className="space-y-5">
        <p role="alert" className="text-sm text-danger">
          {message}
        </p>
        <Button
          className="w-full"
          disabled={pending}
          onClick={async () => {
            if (!onReauthenticate) return void account.refresh();
            setBusy(true);
            setError(undefined);
            try {
              await onReauthenticate();
            } catch (cause) {
              setError(cause instanceof Error ? cause.message : "Could not open sign-in.");
              setBusy(false);
            }
          }}
        >
          {busy ? "Opening sign-in…" : "Sign in again"}
        </Button>
      </div>
    );
  }

  if (state.setupRequired === undefined) {
    return (
      <div className="space-y-5">
        {pending ? (
          <p role="status" className="text-sm text-fg-muted">
            Checking your workspace…
          </p>
        ) : (
          <>
            <p role="alert" className="text-sm text-danger">
              {message ?? "Could not check workspace setup. Please try again."}
            </p>
            <Button className="w-full" onClick={() => void account.refresh()}>
              Try again
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <form
      data-auth-form=""
      aria-label={register ? "Set up account" : "Sign in"}
      aria-busy={pending}
      className="space-y-5"
      onSubmit={async (event) => {
        event.preventDefault();
        if (pending) return;
        const data = new FormData(event.currentTarget);
        setBusy(true);
        setError(undefined);
        try {
          const email = String(data.get("email"));
          const password = String(data.get("password"));
          await (register ? account.signUp(email, password) : account.signIn(email, password));
        } catch (cause) {
          setError(cause instanceof Error ? cause.message : "Could not sign in. Please try again.");
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="block text-sm font-medium">
        Email
        <Input
          className="mt-2"
          name="email"
          type="email"
          autoComplete="email"
          required
          disabled={pending}
        />
      </label>
      <label className="block text-sm font-medium">
        {register ? "Initial password" : "Password"}
        <Input
          className="mt-2"
          name="password"
          type="password"
          autoComplete="current-password"
          minLength={register ? 12 : undefined}
          required
          disabled={pending}
        />
      </label>
      {register && (
        <p className="text-sm text-fg-muted">
          Use the initial password saved in 1Password for this workspace. You don’t need to create a
          new password.
        </p>
      )}
      {message && (
        <p role="alert" className="text-sm text-danger">
          {message}
        </p>
      )}
      {state.phase === "checking" && !busy && (
        <p role="status" className="text-sm text-fg-muted">
          Checking your session…
        </p>
      )}
      <Button type="submit" className="w-full" disabled={pending}>
        {busy ? "Connecting…" : register ? "Create account" : "Sign in"}
      </Button>
    </form>
  );
}
